import React, { useEffect, useState } from 'react';
import { Chip } from './ModuleFrame';
import { createEntity, deleteEntity, updateEntity, friendlyError } from '../data/entities';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/backendApi';

// Knowledge connectors — an `integration` of kind 'knowledge' with a
// provider (SharePoint, Confluence, Google Drive, Dropbox, Notion, S3, website
// crawl), its config, a schedule and a credential. The credential VALUE
// goes to the platform secret store (PUT …/secret), never to Firestore; the
// provider table (fields + credential help) comes from the backend so the
// UI cannot drift from the drivers. Syncs are queue docs the connector
// worker runs; synced documents land in Knowledge as drafts.

const SCHEDULES = [['manual', 'Manual'], ['hourly', 'Hourly'], ['daily', 'Daily'], ['weekly', 'Weekly']];
const slug = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);

export function useConnectorProviders(orgId) {
  const [providers, setProviders] = useState(null);
  useEffect(() => {
    let on = true;
    apiGet(`/integrations/${orgId}/providers`).then((r) => { if (on) setProviders(r.providers || {}); }).catch(() => { if (on) setProviders({}); });
    return () => { on = false; };
  }, [orgId]);
  return providers;
}

export function SyncChip({ integ }) {
  if (integ.syncStatus === 'queued' || integ.syncStatus === 'syncing') return <Chip tone="info">{integ.syncStatus}…</Chip>;
  const s = integ.lastSync;
  if (!s) return <Chip tone="muted">never synced</Chip>;
  if (!s.ok) return <Chip tone="danger" title={s.error || ''}>sync failed · {(s.error || '').slice(0, 60)}</Chip>;
  return <Chip tone="ok" title={new Date(s.at).toLocaleString()}>{s.docs} docs · +{s.added} ~{s.updated}{s.missing ? ` · ${s.missing} gone upstream` : ''}</Chip>;
}

// The provider cards — ready to set up: monogram, one-line blurb, primary
// "Set up". Order is fixed so the panel reads the same everywhere; labels
// render from a local fallback until the provider table arrives.
export const PROVIDER_ORDER = ['sharepoint', 'confluence', 'gdrive', 'dropbox', 'notion', 's3', 'website'];
const FALLBACK = { sharepoint: 'SharePoint', confluence: 'Confluence', gdrive: 'Google Drive', dropbox: 'Dropbox', notion: 'Notion', s3: 'S3 bucket', website: 'Website crawl' };
const MONO = { sharepoint: 'SP', confluence: 'C', gdrive: 'G', dropbox: 'Dx', notion: 'N', s3: 'S3', website: 'W' };
export function ProviderCards({ providers, canConnect, onPick }) {
  return (
    <div className="kc-grid">
      {PROVIDER_ORDER.map((k) => (
        <div key={k} className="kc-card">
          <span className="kc-badge" aria-hidden="true">{MONO[k]}</span>
          <span className="kc-card-title">{providers?.[k]?.label || FALLBACK[k]}</span>
          <span className="kc-card-blurb">{providers?.[k]?.blurb || ''}</span>
          <button type="button" className="cs-btn cs-btn-sm cs-btn-primary kc-card-cta" disabled={!canConnect} title={canConnect ? '' : 'needs integration.update'} onClick={() => onPick(k)}>Set up</button>
        </div>
      ))}
    </div>
  );
}

// Create: pick a provider, name it, fill its fields. Creates the integration
// (kind knowledge) and hands the id back so the editor opens for the secret.
export function NewConnector({ orgId, uid, provider, providers, appId, onDone }) {
  const p = providers?.[provider];
  const [name, setName] = useState(p ? `${p.label}` : '');
  const [config, setConfig] = useState({});
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  if (!p) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const id = await createEntity('integration', { org: orgId }, {
        name: name.trim(), kind: 'knowledge', provider, config, schedule: 'daily', auth: p.needsSecret ? 'apiKey' : 'none',
        secretKey: p.needsSecret ? `KC_${slug(name)}` : '', appIds: appId ? [appId] : [], createdBy: uid,
      });
      onDone(id);
    } catch (ex) { setErr(friendlyError(ex)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mf-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
      <strong>New {p.label} connector</strong>
      <input className="cs-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name — e.g. Policies wiki" aria-label="Name" />
      {p.configFields.map(([k, label, ph]) => (
        <label key={k} className="mf-muted">{label}<input className="cs-input" value={config[k] || ''} onChange={(e) => setConfig((c) => ({ ...c, [k]: e.target.value }))} placeholder={ph} /></label>
      ))}
      {err && <p className="cs-form-error" role="alert" style={{ margin: 0 }}>{err}</p>}
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="submit" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy || !name.trim()}>Create</button>
        <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => onDone(null)}>Cancel</button>
      </div>
      {p.needsSecret && <p className="cs-help" style={{ margin: 0 }}>Next: paste the credential under “Credential”, then Sync now.</p>}
    </form>
  );
}

export default function ConnectorEditor({ orgId, integ, providers, canEdit, app, compact = false }) {
  const p = providers?.[integ.provider];
  const [form, setForm] = useState(integ);
  const [state, setState] = useState('');
  const [secret, setSecret] = useState('');
  const [secretState, setSecretState] = useState('');
  const [busy, setBusy] = useState('');
  useEffect(() => { setForm(integ); setState(''); setSecret(''); setSecretState(''); }, [integ]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCfg = (k, v) => setForm((f) => ({ ...f, config: { ...(f.config || {}), [k]: v } }));
  const dirty = JSON.stringify({ a: form.name, b: form.config, c: form.schedule, d: form.autoPublish, e: form.enabled, f: form.secretKey, g: form.appIds }) !== JSON.stringify({ a: integ.name, b: integ.config, c: integ.schedule, d: integ.autoPublish, e: integ.enabled, f: integ.secretKey, g: integ.appIds });

  const save = async () => {
    setState('saving');
    try {
      await updateEntity('integration', { org: orgId }, integ.id, { name: form.name, config: form.config || {}, schedule: form.schedule || 'daily', autoPublish: !!form.autoPublish, enabled: form.enabled !== false, secretKey: form.secretKey || '', appIds: form.appIds || [], auth: p?.needsSecret || (form.secretKey && integ.provider === 's3') ? 'apiKey' : 'none' });
      setState('saved');
    } catch (e) { setState(friendlyError(e)); }
  };
  const storeSecret = async () => {
    setSecretState('storing');
    try {
      if ((form.secretKey || '') !== (integ.secretKey || '')) throw new Error('save first so the credential key name is on record');
      await apiPut(`/integrations/${orgId}/${integ.id}/secret`, { value: secret });
      setSecret(''); setSecretState('stored');
    } catch (e) { setSecretState(e.message); }
  };
  const clearSecret = async () => {
    if (!window.confirm('Remove the stored credential? Syncs will fail until a new one is stored.')) return;
    setSecretState('storing');
    try { await apiDelete(`/integrations/${orgId}/${integ.id}/secret`); setSecretState('cleared'); } catch (e) { setSecretState(e.message); }
  };
  const run = (what, fn) => async () => {
    setBusy(what); setState('');
    try { await fn(); } catch (e) { setState(e.message); } finally { setBusy(''); }
  };
  const test = run('test', () => apiPost(`/integrations/${orgId}/${integ.id}/test`));
  const sync = run('sync', () => apiPost(`/integrations/${orgId}/${integ.id}/sync`));
  const remove = async () => {
    if (!window.confirm(`Remove ${integ.name}? Documents it synced stay in Knowledge.`)) return;
    await deleteEntity('integration', { org: orgId }, integ.id).catch((e) => setState(friendlyError(e)));
  };
  const linked = app && (form.appIds || []).includes(app.id);
  const needsSecret = p?.needsSecret || integ.provider === 's3';

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Chip tone="muted">{p?.label || integ.provider}</Chip>
        <SyncChip integ={integ} />
        {integ.lastTest && <Chip tone={integ.lastTest.ok ? 'ok' : 'danger'} title={integ.lastTest.error || ''}>{integ.lastTest.ok ? `connects · ${integ.lastTest.ms} ms` : `test failed · ${(integ.lastTest.error || '').slice(0, 60)}`}</Chip>}
        <span style={{ flex: 1 }} />
        <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={!canEdit || !!busy || dirty} title={dirty ? 'save first' : ''} onClick={test}>{busy === 'test' ? 'Testing…' : 'Test connection'}</button>
        <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={!canEdit || !!busy || dirty || integ.enabled === false || ['queued', 'syncing'].includes(integ.syncStatus)} title={dirty ? 'save first' : ''} onClick={sync}>{busy === 'sync' ? 'Queuing…' : 'Sync now'}</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : '1fr 1fr', gap: 8 }}>
        <label className="mf-muted">Name<input className="cs-input" value={form.name || ''} onChange={(e) => set('name', e.target.value)} disabled={!canEdit} /></label>
        <label className="mf-muted">Schedule<select className="cs-input" value={form.schedule || 'daily'} onChange={(e) => set('schedule', e.target.value)} disabled={!canEdit}>{SCHEDULES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
        {(p?.configFields || []).map(([k, label, ph]) => (
          <label key={k} className="mf-muted" style={{ gridColumn: compact ? undefined : 'span 2' }}>{label}<input className="cs-input" value={form.config?.[k] || ''} onChange={(e) => setCfg(k, e.target.value)} placeholder={ph} disabled={!canEdit} /></label>
        ))}
        {needsSecret && (
          <>
            <label className="mf-muted">Credential key <span title="SSM parameter name under /<slug>/pipeline-secrets/">(name)</span><input className="cs-input" value={form.secretKey || ''} onChange={(e) => set('secretKey', e.target.value.replace(/[^A-Za-z0-9_-]/g, ''))} disabled={!canEdit} placeholder="KC_POLICIES" /></label>
            <label className="mf-muted" style={{ gridColumn: compact ? undefined : 'span 2' }}>
              Credential <span className="mf-muted">({p?.secretHelp || 'stored encrypted, never shown again'})</span>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input className="cs-input" type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} disabled={!canEdit || !integ.secretKey} placeholder={integ.secretKey ? (integ.secretSetAt ? 'paste a new value to replace the stored one' : 'paste the credential') : 'save a credential key name first'} style={{ flex: 1 }} />
                <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={!canEdit || !integ.secretKey || secret.trim().length < 4 || secretState === 'storing'} onClick={storeSecret}>{secretState === 'storing' ? 'Storing…' : 'Store'}</button>
                {integ.secretSetAt && <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={!canEdit || secretState === 'storing'} onClick={clearSecret}>Clear</button>}
              </span>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                {integ.secretSetAt ? <Chip tone="ok">credential stored · {new Date(integ.secretSetAt).toLocaleString()}</Chip> : <Chip tone={p?.needsSecret ? 'warn' : 'muted'}>{p?.needsSecret ? 'no credential stored' : 'platform role (no credential)'}</Chip>}
                {secretState && !['storing', 'stored', 'cleared'].includes(secretState) && <span className="cs-form-error">{secretState}</span>}
                {secretState === 'stored' && <span className="mf-muted">Stored. Test the connection, then Sync now.</span>}
              </span>
            </label>
          </>
        )}
        <label className="mf-toggle"><input type="checkbox" checked={form.enabled !== false} onChange={(e) => set('enabled', e.target.checked)} disabled={!canEdit} /> Enabled</label>
        <label className="mf-toggle" title="When upstream content changes, documents that are already live are republished without a new approval. New documents always arrive as drafts."><input type="checkbox" checked={!!form.autoPublish} onChange={(e) => set('autoPublish', e.target.checked)} disabled={!canEdit} /> Republish live docs on change</label>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {canEdit && <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={state === 'saving' || !dirty} onClick={save}>{state === 'saving' ? 'Saving…' : 'Save'}</button>}
        {app && canEdit && (
          <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => set('appIds', linked ? (form.appIds || []).filter((x) => x !== app.id) : [...(form.appIds || []), app.id])}>
            {linked ? `● used by ${app.name}` : `○ link to ${app.name}`}
          </button>
        )}
        {canEdit && <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={remove}>Remove</button>}
        {state && state !== 'saving' && <span className={state === 'saved' ? 'mf-muted' : 'cs-form-error'}>{state === 'saved' ? 'Saved.' : state}</span>}
      </div>
      <p className="cs-help" style={{ margin: 0 }}>Synced documents arrive in Sources as drafts tagged “connector” — submit, approve and publish them like any source. Changed documents get a new version.</p>
    </div>
  );
}
